/**
 * Botões de copiar e enviar o link da sala para o paciente (HNS-1594).
 *
 * O link do paciente é diferente do link de quem conduz: o profissional entra pelo
 * host, o paciente pelo `whereby_sala_url`. Estes dois botões tratam sempre do link
 * do paciente.
 *
 * Existe como componente porque aparece em três lugares — a listagem de agendamentos,
 * a agenda em calendário e a aba de agendamentos dentro do atendimento — e a regra de
 * quando aparecer precisa ser a mesma nos três.
 */
import React, { Component } from "react";
import { Button } from "react-bootstrap";
import { FaRegCopy, FaCheck, FaEnvelope } from "react-icons/fa";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../../services/api";

const MySwal = withReactContent(Swal);

class AcoesLinkPaciente extends Component {
    constructor(props) {
        super(props);
        this.state = { copiado: false, enviando: false };
    }

    componentWillUnmount() {
        if (this.timerCopiado) clearTimeout(this.timerCopiado);
    }

    copiarLink = async () => {
        const { link } = this.props;
        if (!link) return;

        try {
            await navigator.clipboard.writeText(link);
        } catch (e) {
            //clipboard exige contexto seguro; sem ele, cai no seletor temporario
            const campo = document.createElement("textarea");
            campo.value = link;
            campo.style.position = "fixed";
            campo.style.opacity = "0";
            document.body.appendChild(campo);
            campo.select();
            try {
                document.execCommand("copy");
            } finally {
                document.body.removeChild(campo);
            }
        }

        //confirmacao no proprio botao: um alerta para uma acao tao pequena atrapalha
        this.setState({ copiado: true });
        this.timerCopiado = setTimeout(() => this.setState({ copiado: false }), 2500);
    };

    enviarPorEmail = async () => {
        const { agendamentoId, emailPaciente } = this.props;
        if (!agendamentoId) return;

        const confirma = await MySwal.fire({
            title: "Enviar o link ao paciente?",
            html: emailPaciente
                ? `<p>O link da sala será enviado para <strong>${emailPaciente}</strong>.</p>`
                : "<p>O link da sala será enviado para o e-mail do paciente.</p>",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sim, enviar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#3cc172",
        });
        if (!confirma.isConfirmed) return;

        this.setState({ enviando: true });
        try {
            const { data } = await api.post("/meetings/enviar-link-paciente/", {
                agendamento_id: agendamentoId,
            });
            MySwal.fire({
                title: "Link enviado",
                text: data?.detail || "O paciente recebeu o link por e-mail.",
                icon: "success",
            });
        } catch (e) {
            MySwal.fire({
                title: "Não foi possível enviar",
                text: e?.response?.data?.detail || "Tente novamente em instantes.",
                icon: "warning",
            });
        } finally {
            this.setState({ enviando: false });
        }
    };

    render() {
        const { link, agendamentoId, size = "sm", className = "", compacto = false } = this.props;
        const { copiado, enviando } = this.state;

        if (!link) return null;

        //Nos cards o rotulo nao cabe ao lado da acao principal, entao vira icone com title.
        //Botao nativo, e nao <Button>, porque quem estiliza e a classe que o card passa:
        //as variantes do bootstrap brigariam com ela. O estado "copiado" sai por
        //data-copiado, para o CSS decidir a cor sem o componente conhecer a classe.
        if (compacto) {
            return (
                <>
                    <button
                        type="button"
                        className={className}
                        data-copiado={copiado ? "true" : "false"}
                        title={copiado ? "Link copiado" : "Copiar o link de acesso do paciente"}
                        onClick={this.copiarLink}
                    >
                        {copiado ? <FaCheck /> : <FaRegCopy />}
                    </button>
                    {agendamentoId && (
                        <button
                            type="button"
                            className={className}
                            disabled={enviando}
                            title="Enviar o link por e-mail ao paciente"
                            onClick={this.enviarPorEmail}
                        >
                            <FaEnvelope />
                        </button>
                    )}
                </>
            );
        }

        return (
            <>
                <Button
                    variant={copiado ? "success" : "outline-secondary"}
                    size={size}
                    className={className}
                    type="button"
                    title="Copiar o link de acesso do paciente"
                    onClick={this.copiarLink}
                >
                    {copiado ? <><FaCheck className="me-1" /> Link copiado</>
                             : <><FaRegCopy className="me-1" /> Copiar link do paciente</>}
                </Button>
                {agendamentoId && (
                    <Button
                        variant="outline-secondary"
                        size={size}
                        className={className}
                        type="button"
                        disabled={enviando}
                        title="Enviar o link por e-mail ao paciente"
                        onClick={this.enviarPorEmail}
                    >
                        <FaEnvelope className="me-1" />
                        {enviando ? "Enviando..." : "Enviar por e-mail"}
                    </Button>
                )}
            </>
        );
    }
}

export default AcoesLinkPaciente;
